import React, { FC } from 'react';
import styled from 'styled-components';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import Tile from '../../../app/common/components/Tile';
import { COLORS } from '../../../app/common/constants/styles';
import { LinkedService, SocialProvider } from './../types/socialAccounts.types';
import SocialAccountItem from './SocialAccountItem';
import { getTranslatedServiceName } from '../utils/translationMapper';

const CardContent = styled.div`
    padding: 1.5rem;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const ServiceHeader = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid ${COLORS.lightGrey};
`;

const ServiceLogo = styled.div`
    width: 192px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-shrink: 0;
    margin-bottom: 0.5rem;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    @media (max-width: 768px) {
        width: 144px;
        height: 24px;
    }

    @media (max-width: 480px) {
        width: 120px;
        height: 20px;
    }
`;

const MembershipText = styled.p`
    font-size: 0.875rem;
    color: ${COLORS.dark};
    margin: 0;
`;

const SocialAccountsList = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTitle = styled.div`
    font-weight: 600;
    font-size: 14px;
    color: ${COLORS.dark};
    margin-bottom: 1rem;
`;

interface ServiceCardProps extends InjectedIntlProps {
    service: LinkedService;
    unlinkTargetId: string;
    onUnlinkProvider: (
        provider: SocialProvider,
        serviceName: string,
        unlinkTargetId: string,
    ) => void;
    disabled?: boolean;
}

const ServiceCard: FC<ServiceCardProps> = ({
    intl,
    service,
    unlinkTargetId,
    onUnlinkProvider,
    disabled = false,
}) => {
    const translatedServiceName = getTranslatedServiceName(service.serviceName, intl);

    return (
        <Tile qaId={`service-card-${service.serviceUuid}`} localStyles={undefined}>
            <CardContent>
                <ServiceHeader>
                    {service.serviceLogoUrl && (
                        <ServiceLogo>
                            <img src={service.serviceLogoUrl} alt={translatedServiceName} />
                        </ServiceLogo>
                    )}
                    <MembershipText>
                        {TranslationHelpers.getFormattedMessage('rakservice.intro.default', {
                            serviceName: <strong>{translatedServiceName}</strong>,
                        })}
                    </MembershipText>
                </ServiceHeader>

                {service.socialProviders.length > 0 && (
                    <>
                        <SectionTitle>
                            {TranslationHelpers.getFormattedMessage('social.accounts.title')}
                        </SectionTitle>
                        <SocialAccountsList>
                            {service.socialProviders.map((provider) => (
                                <SocialAccountItem
                                    key={provider.providerId}
                                    provider={provider}
                                    onUnlink={(p) =>
                                        onUnlinkProvider(p, service.serviceName, unlinkTargetId)
                                    }
                                    disabled={disabled}
                                    unlinkTargetId={service.rawName}
                                />
                            ))}
                        </SocialAccountsList>
                    </>
                )}
            </CardContent>
        </Tile>
    );
};

export default injectIntl(ServiceCard);
